import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import type { Organization, OrgProject, Lang } from '@/lib/partnerTypes';
import { exportOrganizationPDF, exportOrganizationWord } from '@/lib/exportProject';
import LogoUploader from './components/LogoUploader';
import DocumentsTab from './components/DocumentsTab';
import PhotosTab from './components/PhotosTab';

// ── Date helpers ────────────────────────────────────────────
// Convert "YYYY-MM-DD" → "DD-MM-YYYY" for display
function isoToDDMMYYYY(val: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split('-');
    return `${d}-${m}-${y}`;
  }
  return val;
}
// Convert "DD-MM-YYYY" → "YYYY-MM-DD" for <input type="date"> value
function ddmmyyyyToISO(val: string): string {
  if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
    const [d, m, y] = val.split('-');
    return `${y}-${m}-${d}`;
  }
  return val;
}

const TABS = ['General', 'Contact', 'Description', 'Grant', 'Projects', 'Partners', 'Documents', 'Photos'] as const;
type Tab = typeof TABS[number];

// Fields that count for completeness per tab
const COMPLETENESS_FIELDS: { tab: Tab; keys: (keyof Organization)[] }[] = [
  { tab: 'General', keys: ['name_en', 'name_ro', 'idno', 'region_city', 'registration_date', 'legal_address'] },
  { tab: 'Contact', keys: ['email', 'phone', 'website', 'director_name', 'contact_name'] },
  { tab: 'Description', keys: ['description_en', 'description_ro', 'work_areas_en', 'org_capacity'] },
  { tab: 'Grant', keys: ['grant_themes'] },
  { tab: 'Partners', keys: ['partners_donors'] },
];

function getCompleteness(org: Organization): number {
  const all = COMPLETENESS_FIELDS.flatMap(f => f.keys);
  const filled = all.filter(k => !!(org[k] as string | null)?.trim());
  return Math.round((filled.length / all.length) * 100);
}

function CompletenessBar({ pct }: { pct: number }) {
  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400';
  const label = pct >= 80 ? 'Great!' : pct >= 40 ? 'Almost there' : 'Needs filling';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-stone-400 whitespace-nowrap">{pct}% — {label}</span>
    </div>
  );
}

function PartnerWelcomeBanner({ orgName, canEdit, onEdit }: { orgName: string; canEdit: boolean; onEdit: () => void }) {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('partner_banner_dismissed') === 'true';
  });

  const handleDismiss = () => {
    localStorage.setItem('partner_banner_dismissed', 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div className="bg-[#1a2e4a]/5 border border-[#1a2e4a]/15 rounded-xl px-5 py-4 mb-5 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-[#1a2e4a]/10 rounded-lg flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-[#1a2e4a] text-lg" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1a2e4a]">This is your organization's profile</p>
          <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">
            Here you can manage the public information about <strong>{orgName}</strong>.
            {canEdit && <> Click <strong>Edit</strong> to update any section — changes are saved immediately.</>}
          </p>
          {canEdit && (
            <button
              onClick={onEdit}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a2e4a] bg-[#1a2e4a]/10 hover:bg-[#1a2e4a]/15 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line" />Start Editing
            </button>
          )}
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 flex-shrink-0 cursor-pointer"
        title="Dismiss"
      >
        <i className="ri-close-line text-base" />
      </button>
    </div>
  );
}

function SaveToast({ visible, message, type }: { visible: boolean; message: string; type: 'success' | 'error' }) {
  if (!visible) return null;
  return (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
      <i className={type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
      {message}
    </div>
  );
}

function Field({ label, value, editing, onChange, multiline = false, hint }: {
  label: string; value: string; editing: boolean; onChange?: (v: string) => void; multiline?: boolean; hint?: string;
}) {
  if (editing) return (
    <div>
      <label className="block text-xs font-semibold text-stone-500 mb-1">
        {label}
        {hint && <span className="ml-1 font-normal text-stone-400">— {hint}</span>}
      </label>
      {multiline
        ? <textarea value={value} onChange={e => onChange?.(e.target.value)} rows={4}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20 resize-none" />
        : <input value={value} onChange={e => onChange?.(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20" />}
    </div>
  );
  return (
    <div>
      <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
        {value || <span className="text-stone-300 italic">Not filled in</span>}
      </p>
    </div>
  );
}

// Date field — stores as DD-MM-YYYY, uses native date picker
function DateField({ label, value, editing, onChange }: {
  label: string; value: string; editing: boolean; onChange?: (v: string) => void;
}) {
  const isoVal = ddmmyyyyToISO(value);
  const displayVal = isoToDDMMYYYY(value);

  if (editing) return (
    <div>
      <label className="block text-xs font-semibold text-stone-500 mb-1">
        {label}
        <span className="ml-1 font-normal text-stone-400">— DD-MM-YYYY</span>
      </label>
      <input
        type="date"
        value={isoVal || ''}
        onChange={e => {
          onChange?.(e.target.value ? isoToDDMMYYYY(e.target.value) : '');
        }}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20 cursor-pointer"
      />
    </div>
  );
  return (
    <div>
      <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm text-stone-700">
        {displayVal || <span className="text-stone-300 italic">Not filled in</span>}
      </p>
    </div>
  );
}

function ProjectCard({ project, lang, canEdit, onDelete, onEdit }: { project: OrgProject; lang: Lang; canEdit: boolean; onDelete: (id: string) => void; onEdit: (p: OrgProject) => void }) {
  const name = (lang === 'en' ? project.name_en : project.name_ro) || project.name_en || project.name_ro || 'Untitled';
  const desc = (lang === 'en' ? project.description_en : project.description_ro) || '';

  const yearLabel = project.year
    ? project.year_end && project.year_end !== project.year
      ? `${project.year} – ${project.year_end}`
      : String(project.year)
    : null;

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden group hover:border-[#1a2e4a]/20 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1a2e4a] leading-snug">{name}</p>
            {yearLabel && <span className="text-xs text-stone-400">{yearLabel}</span>}
            {project.donor && <p className="text-xs text-stone-500 mt-1"><span className="font-medium">Donor:</span> {project.donor}</p>}
            {project.budget && (
              <p className="text-xs mt-0.5 flex items-center gap-1">
                <i className="ri-money-dollar-circle-line text-emerald-500" />
                <span className="font-medium text-emerald-700">{project.budget}</span>
              </p>
            )}
            {desc && <p className="text-xs text-stone-400 mt-1 line-clamp-2">{desc}</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canEdit && (
              <button
                onClick={() => onEdit(project)}
                className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 flex items-center justify-center text-[#1a2e4a] hover:bg-[#1a2e4a]/10 rounded-lg cursor-pointer flex-shrink-0"
                title="Edit project"
              >
                <i className="ri-edit-line text-sm" />
              </button>
            )}
            {canEdit && (
              <button onClick={() => onDelete(project.id)} className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer flex-shrink-0">
                <i className="ri-delete-bin-line text-sm" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditProjectModal({ project, onClose, onSaved }: { project: OrgProject; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name_en: project.name_en ?? '',
    name_ro: project.name_ro ?? '',
    year: project.year ? String(project.year) : '',
    year_end: project.year_end ? String(project.year_end) : '',
    donor: project.donor ?? '',
    budget: project.budget ?? '',
    description_en: project.description_en ?? '',
    description_ro: project.description_ro ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.name_en.trim() && !form.name_ro.trim()) { setErr('Please enter project name in at least one language.'); return; }
    if (form.year_end && form.year && parseInt(form.year_end) < parseInt(form.year)) {
      setErr('End year cannot be earlier than start year.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('org_projects').update({
      name_en: form.name_en || null,
      name_ro: form.name_ro || null,
      year: form.year ? parseInt(form.year) : null,
      year_end: form.year_end ? parseInt(form.year_end) : null,
      donor: form.donor || null,
      budget: form.budget || null,
      description_en: form.description_en || null,
      description_ro: form.description_ro || null,
    }).eq('id', project.id);
    if (error) { setErr(error.message); setSaving(false); return; }
    onSaved();
  };

  const inp = (key: keyof typeof form, label: string, placeholder = '') => (
    <div key={key}>
      <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
      <input
        type="text"
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
      />
    </div>
  );

  const textarea = (key: keyof typeof form, label: string) => (
    <div key={key}>
      <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
      <textarea
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        rows={2}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] resize-none"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl w-full max-w-xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-[#1a2e4a]">Edit Project</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"><i className="ri-close-line" /></button>
        </div>

        <div className="mb-5">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Project Info</p>
          <div className="space-y-3">
            {inp('name_en', 'Project Name (EN) *')}
            {inp('name_ro', 'Project Name (RO) *')}
            {/* Implementation Period */}
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-1">
                Period of Implementation
                <span className="ml-1 font-normal text-stone-400">— leave &quot;To&quot; empty if single year</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={form.year}
                  onChange={e => setForm(p => ({ ...p, year: e.target.value }))}
                  placeholder="From (e.g. 2020)"
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
                />
                <span className="text-stone-400 text-sm">–</span>
                <input
                  type="text"
                  value={form.year_end}
                  onChange={e => setForm(p => ({ ...p, year_end: e.target.value }))}
                  placeholder="To (e.g. 2023)"
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
                />
              </div>
            </div>
            {inp('donor', 'Donor / Funder')}
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-1">
                Budget / Grant Amount
                <span className="ml-1 font-normal text-stone-400">— e.g. 50 000 EUR</span>
              </label>
              <input
                type="text"
                value={form.budget}
                onChange={e => setForm(p => ({ ...p, budget: e.target.value }))}
                placeholder="e.g. 50 000 EUR"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
              />
            </div>
            {textarea('description_en', 'Short Description (EN)')}
            {textarea('description_ro', 'Short Description (RO)')}
          </div>
        </div>

        {err && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg mb-4">{err}</p>}

        <div className="flex gap-3 mt-2">
          <button onClick={onClose} className="flex-1 border border-stone-200 text-stone-600 text-sm font-medium rounded-lg py-2 cursor-pointer whitespace-nowrap hover:bg-stone-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#1a2e4a] text-white text-sm font-semibold rounded-lg py-2 disabled:opacity-60 cursor-pointer whitespace-nowrap hover:bg-[#253f62]">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddProjectModal({ orgId, lang, onClose, onAdded }: { orgId: string; lang: Lang; onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({
    name_en: '', name_ro: '', year: '', year_end: '', donor: '', budget: '',
    description_en: '', description_ro: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!form.name_en.trim() && !form.name_ro.trim()) { setErr('Please enter project name in at least one language.'); return; }
    if (form.year_end && form.year && parseInt(form.year_end) < parseInt(form.year)) {
      setErr('End year cannot be earlier than start year.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('org_projects').insert([{
      organization_id: orgId,
      name_en: form.name_en || null,
      name_ro: form.name_ro || null,
      year: form.year ? parseInt(form.year) : null,
      year_end: form.year_end ? parseInt(form.year_end) : null,
      donor: form.donor || null,
      budget: form.budget || null,
      description_en: form.description_en || null,
      description_ro: form.description_ro || null,
    }]);
    if (error) { setErr(error.message); setSaving(false); return; }
    onAdded();
  };

  const inp = (key: keyof typeof form, label: string, placeholder = '') => (
    <div key={key}>
      <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
      <input
        type="text"
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
      />
    </div>
  );

  const textarea = (key: keyof typeof form, label: string) => (
    <div key={key}>
      <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
      <textarea
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        rows={2}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] resize-none"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl w-full max-w-xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-[#1a2e4a]">Add Implemented Project</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"><i className="ri-close-line" /></button>
        </div>

        {/* Section: Basic Info */}
        <div className="mb-5">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Project Info</p>
          <div className="space-y-3">
            {inp('name_en', 'Project Name (EN) *')}
            {inp('name_ro', 'Project Name (RO) *')}
            {/* Implementation Period */}
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-1">
                Period of Implementation
                <span className="ml-1 font-normal text-stone-400">— leave &quot;To&quot; empty if single year</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={form.year}
                  onChange={e => setForm(p => ({ ...p, year: e.target.value }))}
                  placeholder="From (e.g. 2020)"
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
                />
                <span className="text-stone-400 text-sm">–</span>
                <input
                  type="text"
                  value={form.year_end}
                  onChange={e => setForm(p => ({ ...p, year_end: e.target.value }))}
                  placeholder="To (e.g. 2023)"
                  className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
                />
              </div>
            </div>
            {inp('donor', 'Donor / Funder')}
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-1">
                Budget / Grant Amount
                <span className="ml-1 font-normal text-stone-400">— e.g. 50 000 EUR</span>
              </label>
              <input
                type="text"
                value={form.budget}
                onChange={e => setForm(p => ({ ...p, budget: e.target.value }))}
                placeholder="e.g. 50 000 EUR"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a] focus:ring-1 focus:ring-[#1a2e4a]/20"
              />
            </div>
            {textarea('description_en', 'Short Description (EN)')}
            {textarea('description_ro', 'Short Description (RO)')}
          </div>
        </div>

        {err && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg mb-4">{err}</p>}

        <div className="flex gap-3 mt-2">
          <button onClick={onClose} className="flex-1 border border-stone-200 text-stone-600 text-sm font-medium rounded-lg py-2 cursor-pointer whitespace-nowrap hover:bg-stone-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#1a2e4a] text-white text-sm font-semibold rounded-lg py-2 disabled:opacity-60 cursor-pointer whitespace-nowrap hover:bg-[#253f62]">
            {saving ? 'Saving...' : 'Add Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, loading: authLoading, logout } = usePartnerAuth();
  const [org, setOrg] = useState<Organization | null>(null);
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Organization>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: 'success' | 'error' }>({ visible: false, message: '', type: 'success' });
  const [lang, setLang] = useState<Lang>('en');
  const [activeTab, setActiveTab] = useState<Tab>('General');
  const [showAddProject, setShowAddProject] = useState(false);
  const [editingProject, setEditingProject] = useState<OrgProject | null>(null);
  const [orgExporting, setOrgExporting] = useState<'pdf' | 'word' | null>(null);
  const [docsCount, setDocsCount] = useState<number | null>(null);
  const [photosCount, setPhotosCount] = useState<number | null>(null);

  // Track editing state in a ref so loadData can read the latest value
  // without being in its dependency array (prevents resetting unsaved changes)
  const editingRef = useRef(false);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  };

  useEffect(() => {
    if (!authLoading && !profile) navigate('/partner-access', { replace: true });
    if (!authLoading && profile?.role === 'partner' && profile.organization_id !== id) {
      if (profile.organization_id) {
        navigate(`/partner-portal/org/${profile.organization_id}`, { replace: true });
      } else {
        navigate('/partner-portal', { replace: true });
      }
    }
  }, [profile, authLoading, id, navigate]);

  const loadData = useCallback(async () => {
    if (!id) return;
    setPageLoading(true);
    const [
      { data: orgData },
      { data: projData },
      { count: docsCountData },
      { count: photosCountData },
    ] = await Promise.all([
      supabase.from('organizations').select('*').eq('id', id).maybeSingle(),
      supabase.from('org_projects').select('*').eq('organization_id', id).order('year', { ascending: false }),
      supabase.from('org_documents').select('*', { count: 'exact', head: true }).eq('organization_id', id),
      supabase.from('org_photos').select('*', { count: 'exact', head: true }).eq('organization_id', id),
    ]);
    setOrg(orgData ?? null);
    // Only reset editData if the user is NOT currently editing
    // (prevents losing unsaved changes on Supabase token refresh)
    if (!editingRef.current) {
      setEditData(orgData ?? {});
    }
    setProjects(projData ?? []);
    setDocsCount(docsCountData ?? 0);
    setPhotosCount(photosCountData ?? 0);
    setPageLoading(false);
  }, [id]);

  useEffect(() => { if (profile) loadData(); }, [profile, loadData]);

  // canEdit: admin always, partner for their own org, manager for their assigned org
  const canEdit = profile?.role === 'admin'
    || (profile?.role === 'partner' && profile.organization_id === id)
    || (profile?.role === 'manager' && profile.organization_id === id);

  const handleLogoUpdated = (newUrl: string) => {
    setOrg(prev => prev ? { ...prev, logo_url: newUrl } : prev);
    setEditData(prev => ({ ...prev, logo_url: newUrl }));
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    // Convert registration_date from DD-MM-YYYY back to YYYY-MM-DD (ISO) for PostgreSQL
    const savePayload: Partial<Organization> = { ...editData };
    if (typeof savePayload.registration_date === 'string' && savePayload.registration_date) {
      savePayload.registration_date = ddmmyyyyToISO(savePayload.registration_date);
    }
    const { error } = await supabase.from('organizations').update({
      ...savePayload,
      updated_at: new Date().toISOString(),
    }).eq('id', id);
    if (!error) {
      showToast('Changes saved successfully!');
      setEditing(false);
      await loadData();
    } else {
      showToast(`Error: ${error.message}`, 'error');
    }
    setSaving(false);
  };

  const handleDeleteProject = async (pid: string) => {
    await supabase.from('org_projects').delete().eq('id', pid);
    setProjects(prev => prev.filter(p => p.id !== pid));
    showToast('Project removed.');
  };

  const handleExportOrgPDF = async () => {
    if (!org) return;
    setOrgExporting('pdf');
    try { exportOrganizationPDF(org, projects, lang); } finally { setOrgExporting(null); }
  };

  const handleExportOrgWord = async () => {
    if (!org) return;
    setOrgExporting('word');
    try { await exportOrganizationWord(org, projects, lang); } finally { setOrgExporting(null); }
  };

  const handleLogout = async () => { await logout(); navigate('/partner-access', { replace: true }); };

  const set = (key: keyof Organization) => (v: string) => setEditData(prev => ({ ...prev, [key]: v }));
  const val = (key: keyof Organization) => (editing ? (editData[key] as string ?? '') : (org?.[key] as string ?? ''));

  if (authLoading || pageLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="w-8 h-8 border-2 border-[#1a2e4a] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!org || !profile) return null;

  const orgName = lang === 'en' ? org.name_en : org.name_ro;
  const completeness = getCompleteness(org);
  const isPartner = profile.role === 'partner';

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header */}
      <header className="bg-[#1a2e4a] text-white px-6 md:px-10 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {profile.role !== 'partner' && (
            <Link to="/partner-portal" className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" /><span className="hidden sm:block">All Organizations</span>
            </Link>
          )}
          {profile.role !== 'partner' && <div className="w-px h-5 bg-white/20" />}
          <span className="text-sm font-semibold truncate max-w-xs">
            {isPartner ? 'My Organization Profile' : 'Organization Passport'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Completeness badge for partner */}
          {isPartner && (
            <div className={`hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${completeness >= 80 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-400/20 text-amber-300'}`}>
              <i className="ri-bar-chart-line" />
              {completeness}% complete
            </div>
          )}
          <div className="flex items-center bg-white/10 rounded-lg p-0.5">
            {(['en', 'ro'] as Lang[]).map(l => (
              <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${lang === l ? 'bg-white text-[#1a2e4a]' : 'text-white/70 hover:text-white'}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-logout-box-line" /><span className="hidden sm:block">Sign out</span>
          </button>
        </div>
      </header>

      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-8">

        {/* Partner welcome banner */}
        {isPartner && (
          <PartnerWelcomeBanner
            orgName={orgName}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
          />
        )}

        {/* Org header card */}
        <div className="bg-white border border-stone-200 rounded-xl p-6 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <LogoUploader
                orgId={org.id}
                logoUrl={org.logo_url}
                canEdit={canEdit}
                onUpdated={handleLogoUpdated}
              />
              <div>
                <h1 className="text-xl font-bold text-[#1a2e4a]">{orgName}</h1>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {org.region_city && (
                    <span className="text-xs text-stone-400 flex items-center gap-1">
                      <i className="ri-map-pin-line" />{org.region_city}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${org.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'}`}>
                    {org.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {isPartner && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#1a2e4a]/8 text-[#1a2e4a]">
                      <i className="ri-user-line mr-1" />Your Organization
                    </span>
                  )}
                </div>
                {/* Completeness bar - partner only */}
                {isPartner && (
                  <div className="mt-2 w-48">
                    <CompletenessBar pct={completeness} />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Export buttons — always visible */}
              <div className="flex items-center gap-1">
                <button
                  onClick={handleExportOrgPDF}
                  disabled={orgExporting !== null}
                  className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 border border-rose-200 bg-rose-50 hover:bg-rose-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  title="Export to PDF"
                >
                  {orgExporting === 'pdf'
                    ? <i className="ri-loader-4-line animate-spin" />
                    : <i className="ri-file-pdf-2-line" />}
                  PDF
                </button>
                <button
                  onClick={handleExportOrgWord}
                  disabled={orgExporting !== null}
                  className="flex items-center gap-1.5 text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  title="Export to Word"
                >
                  {orgExporting === 'word'
                    ? <i className="ri-loader-4-line animate-spin" />
                    : <i className="ri-file-word-2-line" />}
                  Word
                </button>
              </div>

              {canEdit && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#1a2e4a] px-4 py-2 rounded-lg hover:bg-[#253f62] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line" />Edit Info
                </button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditing(false); setEditData(org); }}
                    className="text-sm font-medium text-stone-500 border border-stone-200 px-3 py-1.5 rounded-lg hover:bg-stone-50 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 text-sm font-semibold text-white bg-emerald-600 px-4 py-1.5 rounded-lg hover:bg-emerald-700 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line" />{saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Edit mode notice */}
          {editing && (
            <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 -mx-6 px-6 -mb-6 py-3 rounded-b-xl">
              <i className="ri-edit-2-line" />
              <span>Editing mode — make your changes and click <strong>Save Changes</strong> when done</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1 mb-4 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-white text-[#1a2e4a]' : 'text-stone-500 hover:text-stone-700'}`}>
              {tab}
              {tab === 'Projects' && projects.length > 0 && (
                <span className="ml-1 text-stone-400">({projects.length})</span>
              )}
              {tab === 'Documents' && docsCount !== null && docsCount > 0 && (
                <span className="ml-1 text-stone-400">({docsCount})</span>
              )}
              {tab === 'Photos' && photosCount !== null && photosCount > 0 && (
                <span className="ml-1 text-stone-400">({photosCount})</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          {activeTab === 'General' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name (English)" value={val('name_en')} editing={editing} onChange={set('name_en')} hint="official name" />
              <Field label="Name (Romanian)" value={val('name_ro')} editing={editing} onChange={set('name_ro')} hint="official name" />
              <Field label="IDNO" value={val('idno')} editing={editing} onChange={set('idno')} hint="identification number" />
              <Field label="Region / City" value={val('region_city')} editing={editing} onChange={set('region_city')} />
              <DateField label="Registration Date" value={val('registration_date')} editing={editing} onChange={set('registration_date')} />
              <div className="sm:col-span-2">
                <Field label="Legal Address" value={val('legal_address')} editing={editing} onChange={set('legal_address')} />
              </div>
              {editing && (
                <div>
                  <label className="block text-xs font-semibold text-stone-500 mb-1">Status</label>
                  <select
                    value={(editData.status as string) ?? 'active'}
                    onChange={e => setEditData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1a2e4a]"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Contact' && (
            <div className="space-y-6">
              {/* General contacts */}
              <div>
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-4">Organization Contacts</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Email" value={val('email')} editing={editing} onChange={set('email')} />
                  <Field label="Phone" value={val('phone')} editing={editing} onChange={set('phone')} />
                  <div className="sm:col-span-2">
                    <Field label="Website" value={val('website')} editing={editing} onChange={set('website')} hint="full URL with https://" />
                  </div>
                  <Field label="Facebook" value={val('social_facebook')} editing={editing} onChange={set('social_facebook')} hint="page URL" />
                  <Field label="Instagram" value={val('social_instagram')} editing={editing} onChange={set('social_instagram')} hint="profile URL" />
                  <Field label="LinkedIn" value={val('social_linkedin')} editing={editing} onChange={set('social_linkedin')} hint="profile URL" />
                </div>
              </div>

              <div className="border-t border-stone-100" />

              {/* Director / Manager */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#1a2e4a]/8 rounded-lg flex-shrink-0">
                    <i className="ri-user-star-line text-[#1a2e4a] text-sm" />
                  </div>
                  <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Director / Manager</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full Name (ФИО)" value={val('director_name')} editing={editing} onChange={set('director_name')} />
                  <Field label="Position / Title" value={val('director_position')} editing={editing} onChange={set('director_position')} />
                  <Field label="Phone" value={val('director_phone')} editing={editing} onChange={set('director_phone')} />
                  <Field label="E-mail" value={val('director_email')} editing={editing} onChange={set('director_email')} />
                </div>
              </div>

              <div className="border-t border-stone-100" />

              {/* Contact Person (Projects) */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className="ri-contacts-line text-amber-600 text-sm" />
                  </div>
                  <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">Contact Person (Projects)</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full Name (ФИО)" value={val('contact_name')} editing={editing} onChange={set('contact_name')} />
                  <Field label="Position / Title" value={val('contact_position')} editing={editing} onChange={set('contact_position')} />
                  <Field label="Phone" value={val('contact_phone')} editing={editing} onChange={set('contact_phone')} />
                  <Field label="E-mail" value={val('contact_email')} editing={editing} onChange={set('contact_email')} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Description' && (
            <div className="space-y-5">
              <Field label="Description (English)" value={val('description_en')} editing={editing} onChange={set('description_en')} multiline hint="mission, vision, history" />
              <Field label="Description (Romanian)" value={val('description_ro')} editing={editing} onChange={set('description_ro')} multiline hint="misiune, viziune, istoric" />

              <div className="border-t border-stone-100 pt-5 space-y-5">
                <Field
                  label="Capacitate organizațională"
                  value={val('org_capacity')}
                  editing={editing}
                  onChange={set('org_capacity')}
                  multiline
                  hint="personal, voluntari, birou, infrastructură"
                />
              </div>

              <div className="border-t border-stone-100 pt-5 space-y-5">
                <Field label="Areas of Work (EN)" value={val('work_areas_en')} editing={editing} onChange={set('work_areas_en')} multiline hint="e.g. education, health, environment" />
                <Field label="Areas of Work (RO)" value={val('work_areas_ro')} editing={editing} onChange={set('work_areas_ro')} multiline hint="ex. educație, sănătate, mediu" />
              </div>
            </div>
          )}

          {activeTab === 'Grant' && (
            <div className="space-y-5">
              <div className="flex items-start gap-3 bg-amber-50/60 border border-amber-100 rounded-xl px-4 py-3">
                <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-award-line text-amber-600 text-sm" />
                </div>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Indicați temele preconizate pentru proiectele de grant pe care organizația intenționează să le depună.
                </p>
              </div>
              <Field
                label="Tema (temele) preconizată(e) pentru proiectul (proiectele) de grant"
                value={val('grant_themes')}
                editing={editing}
                onChange={set('grant_themes')}
                multiline
                hint="ex. educație, tineret, mediu, drepturile omului"
              />
            </div>
          )}

          {activeTab === 'Projects' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-sm font-bold text-[#1a2e4a]">Implemented Projects</h3>
                  <p className="text-xs text-stone-400 mt-0.5">{projects.length} project{projects.length !== 1 ? 's' : ''} added</p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setShowAddProject(true)}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-[#1a2e4a] text-white px-3 py-2 rounded-lg hover:bg-[#253f62] cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" />Add Project
                  </button>
                )}
              </div>
              {projects.length === 0 ? (
                <div className="py-12 text-center border-2 border-dashed border-stone-200 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
                    <i className="ri-folder-chart-line text-stone-400 text-xl" />
                  </div>
                  <p className="text-sm font-medium text-stone-500">No projects yet</p>
                  {canEdit && <p className="text-xs text-stone-400 mt-1">Click <strong>Add Project</strong> to list your implemented projects</p>}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {projects.map(p => (
                    <ProjectCard key={p.id} project={p} lang={lang} canEdit={canEdit} onDelete={handleDeleteProject} onEdit={setEditingProject} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'Partners' && (
            <div>
              {isPartner && !editing && !org.partners_donors && canEdit && (
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                  <i className="ri-lightbulb-line text-amber-500 mt-0.5" />
                  <p className="text-xs text-stone-500 leading-relaxed">
                    List your current partners, donors and collaborating organizations here. This section helps other organizations discover potential collaboration opportunities.
                  </p>
                </div>
              )}
              <Field
                label="Partners & Donors"
                value={val('partners_donors')}
                editing={editing}
                onChange={set('partners_donors')}
                multiline
                hint="names of donors, partners, collaborators"
              />
            </div>
          )}

          {activeTab === 'Documents' && id && (
            <DocumentsTab orgId={id} canEdit={canEdit} onCountChange={setDocsCount} />
          )}

          {activeTab === 'Photos' && id && (
            <PhotosTab orgId={id} canEdit={canEdit} onCountChange={setPhotosCount} />
          )}
        </div>
      </div>

      {showAddProject && id && (
        <AddProjectModal
          orgId={id}
          lang={lang}
          onClose={() => setShowAddProject(false)}
          onAdded={() => { setShowAddProject(false); loadData(); showToast('Project added!'); }}
        />
      )}

      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={() => { setEditingProject(null); loadData(); showToast('Project updated!'); }}
        />
      )}

      <SaveToast visible={toast.visible} message={toast.message} type={toast.type} />
    </div>
  );
}
